import React, { useState, useEffect, useMemo } from "react";
import { 
  X, 
  Save, 
  Settings2, 
  Disc, 
  Thermometer, 
  Clock, 
  Scale, 
  Gem, 
  Plus, 
  Trash2, 
  RefreshCw, 
  Info,
  CheckCircle2,
  Minus,
  RotateCcw,
  Copy,
  Zap,
  BookmarkCheck,
  Search
} from "lucide-react";
import { produtoDadosService, extractConfigBorracha } from "../../services/produtoDadosService";
import { MODELOS_CANONICOS, resolveModeloJoia, isModeloComMedida } from "../../utils/skuUtils";
import { AROS_PADRAO_GRADE, AROS_MAIS_VENDIDOS, AROS_PARES } from "../../utils/moldRingUtils";

// Sugestões populares para inserção rápida de zircônias e pedras
const POPULAR_STONES_PRESETS = [
  { tipo: "Zircônia Redonda", tamanho: "1.00mm", cor: "Branca" },
  { tipo: "Zircônia Redonda", tamanho: "1.25mm", cor: "Branca" },
  { tipo: "Zircônia Redonda", tamanho: "1.50mm", cor: "Branca" },
  { tipo: "Zircônia Redonda", tamanho: "1.75mm", cor: "Branca" },
  { tipo: "Zircônia Redonda", tamanho: "2.00mm", cor: "Branca" },
  { tipo: "Zircônia Redonda", tamanho: "2.50mm", cor: "Branca" },
  { tipo: "Zircônia Redonda", tamanho: "1.50mm", cor: "Verde Esmeralda" },
  { tipo: "Zircônia Redonda", tamanho: "1.50mm", cor: "Rubi" },
  { tipo: "Zircônia Redonda", tamanho: "1.50mm", cor: "Preta / Black" }
];

const QUICK_TEMPS = ["68", "69", "70", "71", "72"];
const QUICK_VACUOS = ["2", "3", "4", "5"];

const PREMADES_STORAGE_KEY = "semprejoias_borracha_custom_premades_v1";

export const DEFAULT_PREMADES_BY_TIPO = {
  "Anel": {
    codigo_maquina: "1223-1",
    temperatura_tanque: "69",
    temperatura_bico: "71",
    tempo_vacuo: "3",
    peso_cera_g: "0.85",
    qtd_moldes: "1",
    aros_disponiveis: [14, 16, 18, 20, 22],
    tolerancia_ajuste_bancada: 1,
    observacoes: "Injeção padrão para anéis. Cuidar alinhamento do aro e garras.",
    pedras_padrao: []
  },
  "Pingente": {
    codigo_maquina: "2234-1",
    temperatura_tanque: "70",
    temperatura_bico: "72",
    tempo_vacuo: "4",
    peso_cera_g: "1.10",
    qtd_moldes: "1",
    observacoes: "Cuidar com canal de entrada no passador para não criar rebarba.",
    pedras_padrao: []
  },
  "Medalha": {
    codigo_maquina: "2344-1",
    temperatura_tanque: "71",
    temperatura_bico: "73",
    tempo_vacuo: "4",
    peso_cera_g: "1.40",
    qtd_moldes: "1",
    observacoes: "Evitar marcas de contração na face frontal de relevo.",
    pedras_padrao: []
  },
  "Berloque": {
    codigo_maquina: "2334-2",
    temperatura_tanque: "70",
    temperatura_bico: "72",
    tempo_vacuo: "4",
    peso_cera_g: "0.95",
    qtd_moldes: "2",
    observacoes: "Molde duplo. Resfriar 15s antes de abrir para não deformar o furo passador.",
    pedras_padrao: []
  },
  "Brinco": {
    codigo_maquina: "1223-1",
    temperatura_tanque: "69",
    temperatura_bico: "71",
    tempo_vacuo: "3",
    peso_cera_g: "0.60",
    qtd_moldes: "2",
    observacoes: "Par de brincos. Manter pino de tarraxa limpo e alinhado.",
    pedras_padrao: []
  },
  "Pulseira": {
    codigo_maquina: "2234-1",
    temperatura_tanque: "70",
    temperatura_bico: "72",
    tempo_vacuo: "4",
    peso_cera_g: "1.80",
    qtd_moldes: "1",
    observacoes: "Atenção redobrada às articulações e elos vazados.",
    pedras_padrao: []
  },
  "Colar / Corrente": {
    codigo_maquina: "1223-1",
    temperatura_tanque: "70",
    temperatura_bico: "71",
    tempo_vacuo: "3",
    peso_cera_g: "1.20",
    qtd_moldes: "1",
    observacoes: "Fechos e terminais. Cuidar pressão na área da mola.",
    pedras_padrao: []
  },
  "Outros": {
    codigo_maquina: "1223-1",
    temperatura_tanque: "69",
    temperatura_bico: "71",
    tempo_vacuo: "3",
    peso_cera_g: "1.00",
    qtd_moldes: "1",
    observacoes: "",
    pedras_padrao: []
  }
};

export function getPremadeForTipo(tipo) {
  try {
    const raw = localStorage.getItem(PREMADES_STORAGE_KEY);
    if (raw) {
      const customMap = JSON.parse(raw);
      if (customMap && customMap[tipo]) {
        return customMap[tipo];
      }
    }
  } catch (_) {}
  return DEFAULT_PREMADES_BY_TIPO[tipo] || DEFAULT_PREMADES_BY_TIPO["Outros"];
}

export function savePremadeForTipo(tipo, config) {
  try {
    let customMap = {};
    const raw = localStorage.getItem(PREMADES_STORAGE_KEY);
    if (raw) {
      customMap = JSON.parse(raw) || {};
    }
    customMap[tipo] = {
      codigo_maquina: config.codigo_maquina || "",
      temperatura_tanque: config.temperatura_tanque || "",
      temperatura_bico: config.temperatura_bico || "",
      tempo_vacuo: config.tempo_vacuo || "",
      peso_cera_g: config.peso_cera_g || "",
      qtd_moldes: config.qtd_moldes !== undefined ? String(config.qtd_moldes) : "1",
      aros_disponiveis: Array.isArray(config.aros_disponiveis) ? config.aros_disponiveis : [],
      tolerancia_ajuste_bancada: config.tolerancia_ajuste_bancada !== undefined ? config.tolerancia_ajuste_bancada : 1,
      observacoes: config.observacoes || "",
      pedras_padrao: Array.isArray(config.pedras_padrao) ? config.pedras_padrao : []
    };
    localStorage.setItem(PREMADES_STORAGE_KEY, JSON.stringify(customMap));
    return true;
  } catch (e) {
    console.warn("Erro ao salvar premade no localStorage:", e);
    return false;
  }
}

export default function ConfigBorrachaModal({ 
  isOpen, 
  onClose, 
  skuPai, 
  initialConfig, 
  onSuccess,
  userName = "Operador",
  allMoldes = null
}) {
  const [formData, setFormData] = useState({
    codigo_maquina: "",
    temperatura_tanque: "",
    temperatura_bico: "",
    tempo_vacuo: "",
    peso_cera_g: "",
    qtd_moldes: "1",
    tipo_modelo: "",
    observacoes: "",
    pedras_padrao: [],
    aros_disponiveis: [],
    tolerancia_ajuste_bancada: 1
  });

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Estado para a gaveta de copiar de outro produto
  const [showCopyDrawer, setShowCopyDrawer] = useState(false);
  const [searchTermCopy, setSearchTermCopy] = useState("");
  const [moldesCandidates, setMoldesCandidates] = useState(allMoldes || []);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Inicializa dados do formulário
  useEffect(() => {
    if (!isOpen || !skuPai) return;

    setErrorMsg("");
    setSuccessMsg("");

    const config = initialConfig ? initialConfig : null;
    if (config) {
      setFormData({
        codigo_maquina: config.codigo_maquina || "",
        temperatura_tanque: config.temperatura_tanque || "",
        temperatura_bico: config.temperatura_bico || "",
        tempo_vacuo: config.tempo_vacuo || "",
        peso_cera_g: config.peso_cera_g || "",
        qtd_moldes: config.qtd_moldes !== undefined ? String(config.qtd_moldes) : "1",
        tipo_modelo: config.tipo_modelo || resolveModeloJoia(skuPai),
        aros_disponiveis: Array.isArray(config.aros_disponiveis) ? config.aros_disponiveis.map(Number) : [],
        tolerancia_ajuste_bancada: typeof config.tolerancia_ajuste_bancada === "number" ? config.tolerancia_ajuste_bancada : 1,
        observacoes: config.observacoes || "",
        pedras_padrao: Array.isArray(config.pedras_padrao) 
          ? config.pedras_padrao.map(p => ({ ...p, qtd: p.qtd !== undefined ? p.qtd : 1 })) 
          : []
      });
    } else {
      // Busca dados frescos se não vier por prop
      let active = true;
      produtoDadosService.getProdutoDados(skuPai).then(rec => {
        if (!active) return;
        const fresh = extractConfigBorracha(rec);
        setFormData({
          codigo_maquina: fresh.codigo_maquina || "",
          temperatura_tanque: fresh.temperatura_tanque || "",
          temperatura_bico: fresh.temperatura_bico || "",
          tempo_vacuo: fresh.tempo_vacuo || "",
          peso_cera_g: fresh.peso_cera_g || "",
          qtd_moldes: fresh.qtd_moldes !== undefined ? String(fresh.qtd_moldes) : "1",
          tipo_modelo: fresh.tipo_modelo || resolveModeloJoia(skuPai, null, rec),
          aros_disponiveis: Array.isArray(fresh.aros_disponiveis) ? fresh.aros_disponiveis.map(Number) : [],
          tolerancia_ajuste_bancada: typeof fresh.tolerancia_ajuste_bancada === "number" ? fresh.tolerancia_ajuste_bancada : 1,
          observacoes: fresh.observacoes || "",
          pedras_padrao: Array.isArray(fresh.pedras_padrao) 
            ? fresh.pedras_padrao.map(p => ({ ...p, qtd: p.qtd !== undefined ? p.qtd : 1 })) 
            : []
        });
      }).catch(err => {
        console.warn("Erro ao buscar config inicial da borracha:", err);
      });

      return () => { active = false; };
    }
  }, [isOpen, skuPai, initialConfig]);

  if (!isOpen) return null;

  // Carrega lista de moldes para copiar caso não venha pronta
  useEffect(() => {
    if (showCopyDrawer && (!moldesCandidates || moldesCandidates.length === 0)) {
      setLoadingCandidates(true);
      produtoDadosService.getAllMoldesWithEstoque()
        .then(list => {
          setMoldesCandidates(list || []);
        })
        .catch(err => {
          console.warn("Erro ao buscar moldes para cópia:", err);
        })
        .finally(() => setLoadingCandidates(false));
    }
  }, [showCopyDrawer, moldesCandidates]);

  // Filtra candidatos para copiar (exclui o SKU atual)
  const filteredCandidatesToCopy = useMemo(() => {
    const cleanCurrent = (skuPai || "").toUpperCase().trim();
    const list = (moldesCandidates || []).filter(m => {
      const p = (m.skuPai || "").toUpperCase().trim();
      return p && p !== cleanCurrent;
    });

    if (!searchTermCopy.trim()) return list;
    const term = searchTermCopy.toLowerCase().trim();
    return list.filter(m => {
      const p = (m.skuPai || "").toLowerCase();
      const cfg = m.config_borracha || {};
      const mod = (cfg.tipo_modelo || "").toLowerCase();
      const cod = (cfg.codigo_maquina || "").toLowerCase();
      return p.includes(term) || mod.includes(term) || cod.includes(term);
    });
  }, [moldesCandidates, skuPai, searchTermCopy]);

  // Copia os parâmetros de outro molde
  const handleCopyFromMolde = (molde) => {
    const srcConfig = molde.config_borracha || extractConfigBorracha(molde);
    if (!srcConfig) return;

    setFormData(prev => ({
      ...prev,
      codigo_maquina: srcConfig.codigo_maquina !== undefined ? srcConfig.codigo_maquina : prev.codigo_maquina,
      temperatura_tanque: srcConfig.temperatura_tanque !== undefined ? srcConfig.temperatura_tanque : prev.temperatura_tanque,
      temperatura_bico: srcConfig.temperatura_bico !== undefined ? srcConfig.temperatura_bico : prev.temperatura_bico,
      tempo_vacuo: srcConfig.tempo_vacuo !== undefined ? srcConfig.tempo_vacuo : prev.tempo_vacuo,
      peso_cera_g: srcConfig.peso_cera_g !== undefined ? srcConfig.peso_cera_g : prev.peso_cera_g,
      qtd_moldes: srcConfig.qtd_moldes !== undefined ? String(srcConfig.qtd_moldes) : prev.qtd_moldes,
      tipo_modelo: srcConfig.tipo_modelo || prev.tipo_modelo,
      aros_disponiveis: Array.isArray(srcConfig.aros_disponiveis) ? srcConfig.aros_disponiveis : prev.aros_disponiveis,
      tolerancia_ajuste_bancada: srcConfig.tolerancia_ajuste_bancada !== undefined ? srcConfig.tolerancia_ajuste_bancada : prev.tolerancia_ajuste_bancada,
      observacoes: srcConfig.observacoes !== undefined ? srcConfig.observacoes : prev.observacoes,
      pedras_padrao: Array.isArray(srcConfig.pedras_padrao) && srcConfig.pedras_padrao.length > 0
        ? srcConfig.pedras_padrao.map(p => ({ ...p, qtd: p.qtd !== undefined ? p.qtd : 1 }))
        : prev.pedras_padrao
    }));

    setShowCopyDrawer(false);
    setSuccessMsg(`Configuração copiada do SKU ${molde.skuPai}!`);
  };

  // Carrega padrão (premade) para um modelo específico
  const handleLoadPremade = (tipoToLoad = null) => {
    const targetTipo = tipoToLoad || formData.tipo_modelo || "Anel";
    const premade = getPremadeForTipo(targetTipo);
    if (!premade) return;

    setFormData(prev => ({
      ...prev,
      tipo_modelo: targetTipo,
      codigo_maquina: premade.codigo_maquina !== undefined ? premade.codigo_maquina : prev.codigo_maquina,
      temperatura_tanque: premade.temperatura_tanque !== undefined ? premade.temperatura_tanque : prev.temperatura_tanque,
      temperatura_bico: premade.temperatura_bico !== undefined ? premade.temperatura_bico : prev.temperatura_bico,
      tempo_vacuo: premade.tempo_vacuo !== undefined ? premade.tempo_vacuo : prev.tempo_vacuo,
      peso_cera_g: premade.peso_cera_g !== undefined ? premade.peso_cera_g : prev.peso_cera_g,
      qtd_moldes: premade.qtd_moldes !== undefined ? String(premade.qtd_moldes) : prev.qtd_moldes,
      aros_disponiveis: premade.aros_disponiveis !== undefined ? premade.aros_disponiveis : prev.aros_disponiveis,
      tolerancia_ajuste_bancada: premade.tolerancia_ajuste_bancada !== undefined ? premade.tolerancia_ajuste_bancada : prev.tolerancia_ajuste_bancada,
      observacoes: premade.observacoes !== undefined ? premade.observacoes : prev.observacoes,
      pedras_padrao: Array.isArray(premade.pedras_padrao) && premade.pedras_padrao.length > 0
        ? premade.pedras_padrao.map(p => ({ ...p, qtd: p.qtd !== undefined ? p.qtd : 1 }))
        : prev.pedras_padrao
    }));

    setSuccessMsg(`Padrão para "${targetTipo}" aplicado com sucesso!`);
  };

  // Salva valores atuais como novo padrão para este tipo de modelo
  const handleSaveAsPremade = () => {
    const targetTipo = formData.tipo_modelo || "Anel";
    const ok = savePremadeForTipo(targetTipo, formData);
    if (ok) {
      setSuccessMsg(`Configuração salva como padrão para ${targetTipo}!`);
    } else {
      setErrorMsg("Não foi possível salvar o padrão no dispositivo.");
    }
  };

  // Extrai os 6 dígitos para o painel visual
  const digits = (formData.codigo_maquina || "").padEnd(6, " ").slice(0, 6).split("");

  // Permite alterar um dígito específico diretamente pelo painel
  const handleCycleDigit = (index) => {
    const currentList = (formData.codigo_maquina || "").padEnd(6, "-").slice(0, 6).split("");
    const char = currentList[index];
    const order = ["-", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
    let nextIdx = (order.indexOf(char) + 1) % order.length;
    currentList[index] = order[nextIdx];
    setFormData(prev => ({ ...prev, codigo_maquina: currentList.join("") }));
  };

  // Adiciona nova linha de pedra padrão
  const handleAddPedra = (preset = null) => {
    setFormData(prev => ({
      ...prev,
      pedras_padrao: [
        ...prev.pedras_padrao,
        preset 
          ? { ...preset, qtd: 1 } 
          : { qtd: 1, tipo: "Zircônia Redonda", tamanho: "1.50mm", cor: "Branca" }
      ]
    }));
  };

  // Remove linha de pedra
  const handleRemovePedra = (index) => {
    setFormData(prev => ({
      ...prev,
      pedras_padrao: prev.pedras_padrao.filter((_, i) => i !== index)
    }));
  };

  // Edita campo de pedra — sem travar quando apaga!
  const handleUpdatePedra = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      pedras_padrao: prev.pedras_padrao.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!skuPai) return;

    setSaving(true);
    setErrorMsg("");

    try {
      const cleanCodigo = formData.codigo_maquina.trim().toUpperCase();

      // Normaliza pedras garantindo que quantidades vazias virem pelo menos 1 ao salvar
      const cleanedPedras = formData.pedras_padrao
        .map(p => ({
          ...p,
          qtd: Math.max(1, parseInt(p.qtd, 10) || 1),
          tipo: (p.tipo || "Zircônia").trim(),
          tamanho: (p.tamanho || "").trim(),
          cor: (p.cor || "Branca").trim()
        }))
        .filter(p => p.tipo);

      const payload = {
        codigo_maquina: cleanCodigo,
        temperatura_tanque: formData.temperatura_tanque.trim(),
        temperatura_bico: formData.temperatura_bico.trim(),
        tempo_vacuo: formData.tempo_vacuo.trim(),
        peso_cera_g: formData.peso_cera_g.trim(),
        qtd_moldes: String(parseInt(formData.qtd_moldes, 10) || 1),
        tipo_modelo: formData.tipo_modelo || "Outros",
        aros_disponiveis: (formData.aros_disponiveis || []).map(Number).filter(n => !isNaN(n)).sort((a,b) => a - b),
        tolerancia_ajuste_bancada: parseInt(formData.tolerancia_ajuste_bancada, 10) || 0,
        observacoes: formData.observacoes.trim(),
        pedras_padrao: cleanedPedras
      };

      const res = await produtoDadosService.updateConfigBorracha(skuPai, payload, userName);

      setSuccessMsg("Configurações salvas com sucesso!");
      setTimeout(() => {
        if (onSuccess) onSuccess(res?.configBorracha || payload);
        onClose();
      }, 500);
    } catch (err) {
      console.error("Erro ao salvar configuração de borracha:", err);
      setErrorMsg(err.message || "Não foi possível salvar os parâmetros da borracha.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div 
        className="bg-white border border-slate-200 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[94vh] animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* CABEÇALHO */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-amber-950 p-4 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/20 border border-amber-400/30 text-amber-400 rounded-xl shadow-inner">
              <Disc size={22} className="animate-spin-slow" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-black uppercase tracking-wider text-white">
                  Configuração da Borracha
                </h2>
                <span className="font-mono text-xs bg-amber-400/20 text-amber-300 px-2 py-0.5 rounded border border-amber-400/30 font-bold">
                  {skuPai}
                </span>
              </div>
              <p className="text-[11px] text-slate-300 mt-0.5">
                Parâmetros específicos da injetora e receitas de montagem para este molde
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition cursor-pointer"
            title="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* BARRA DE ATALHOS RÁPIDOS: COPIAR DE OUTRO SKU E CARREGAR/SALVAR PREMADES */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50/50 border-b border-amber-200/80 px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Botão Copiar de Outro Produto */}
            <button
              type="button"
              onClick={() => setShowCopyDrawer(!showCopyDrawer)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition active:scale-95 cursor-pointer shadow-2xs ${
                showCopyDrawer 
                  ? "bg-amber-600 text-white shadow-xs" 
                  : "bg-white hover:bg-amber-100/60 text-slate-800 border border-amber-200"
              }`}
              title="Copiar configurações de outro SKU já cadastrado"
            >
              <Copy size={13} className={showCopyDrawer ? "text-white" : "text-amber-600"} />
              <span>Copiar de Outro SKU</span>
            </button>

            {/* Botão Carregar Padrão do Tipo */}
            <button
              type="button"
              onClick={() => handleLoadPremade()}
              className="px-3 py-1.5 bg-white hover:bg-amber-100/60 border border-amber-200 text-amber-900 rounded-lg text-xs font-bold flex items-center gap-1.5 transition active:scale-95 cursor-pointer shadow-2xs"
              title={`Carregar parâmetros padrão de ${formData.tipo_modelo || "modelo"}`}
            >
              <Zap size={13} className="text-amber-600 fill-amber-600" />
              <span>Carregar Padrão ({formData.tipo_modelo || "Modelo"})</span>
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            {/* Botão Salvar como Padrão do Tipo */}
            <button
              type="button"
              onClick={handleSaveAsPremade}
              className="px-3 py-1.5 bg-white hover:bg-purple-50 border border-purple-200 text-purple-800 rounded-lg text-xs font-bold flex items-center gap-1.5 transition active:scale-95 cursor-pointer shadow-2xs"
              title={`Salvar estes valores como novo padrão para todos os ${formData.tipo_modelo || "produtos deste tipo"}`}
            >
              <BookmarkCheck size={13} className="text-purple-600" />
              <span>Salvar como Padrão de {formData.tipo_modelo || "Tipo"}</span>
            </button>
          </div>
        </div>

        {/* GAVETA EXPANSÍVEL DE COPIAR DE OUTRO SKU */}
        {showCopyDrawer && (
          <div className="bg-amber-50/95 border-b border-amber-300 p-3 sm:p-4 space-y-2.5 animate-in fade-in duration-150 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Copy size={15} className="text-amber-700" />
                <h3 className="text-xs font-black uppercase text-amber-950">
                  Selecione um Produto para Copiar as Configurações
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCopyDrawer(false)}
                className="text-xs text-amber-800 hover:text-amber-950 font-bold px-2 py-0.5 rounded hover:bg-amber-200/60 cursor-pointer"
              >
                ✕ Fechar
              </button>
            </div>

            <div className="relative">
              <Search size={14} className="absolute left-3 top-2.5 text-amber-600" />
              <input
                type="text"
                autoFocus
                placeholder="Buscar SKU cadastrado (ex: MEDPIN, ANEL, PINGENTE, COR)..."
                value={searchTermCopy}
                onChange={(e) => setSearchTermCopy(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-white border border-amber-300 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500 uppercase"
              />
            </div>

            <div className="max-h-44 overflow-y-auto space-y-1.5 custom-scrollbar pr-1">
              {filteredCandidatesToCopy.length === 0 ? (
                <div className="text-center py-3 text-xs text-slate-500 bg-white/70 rounded-lg border border-dashed border-amber-300">
                  {loadingCandidates ? "Carregando moldes cadastrados..." : "Nenhum outro SKU com borracha encontrado para o termo digitado."}
                </div>
              ) : (
                filteredCandidatesToCopy.map((m) => {
                  const cfg = m.config_borracha || extractConfigBorracha(m);
                  return (
                    <div
                      key={m.skuPai || m.id}
                      className="flex items-center justify-between gap-2 p-2 bg-white hover:bg-amber-100/60 border border-amber-200 rounded-lg transition shadow-2xs"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-black text-xs text-slate-900">{m.skuPai}</span>
                          {cfg.tipo_modelo && (
                            <span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.2 rounded font-bold border border-slate-200">
                              {cfg.tipo_modelo}
                            </span>
                          )}
                          {cfg.codigo_maquina && (
                            <span className="font-mono text-[10px] font-black bg-amber-100 text-amber-900 px-1.5 py-0.2 rounded border border-amber-300">
                              Código: {cfg.codigo_maquina}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                          {cfg.temperatura_tanque && <span>T.Tanque: {cfg.temperatura_tanque}°C</span>}
                          {cfg.temperatura_bico && <span>T.Bico: {cfg.temperatura_bico}°C</span>}
                          {cfg.tempo_vacuo && <span>Vácuo: {cfg.tempo_vacuo}s</span>}
                          {cfg.qtd_moldes && <span>Moldes: {cfg.qtd_moldes}x</span>}
                          {Array.isArray(cfg.pedras_padrao) && cfg.pedras_padrao.length > 0 && (
                            <span className="text-purple-600 font-bold">💎 {cfg.pedras_padrao.length} pedras</span>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleCopyFromMolde(m)}
                        className="px-2.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-black shadow-2xs transition active:scale-95 cursor-pointer shrink-0"
                      >
                        Copiar Dados
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* FORMULÁRIO */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar">
          
          {errorMsg && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 px-3.5 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2">
              <X size={16} className="text-rose-600 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-3.5 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* PAINEL PRINCIPAL: CÓDIGO DA MÁQUINA (6 DÍGITOS) */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50/60 border border-amber-200 rounded-xl p-4 space-y-3 shadow-xs">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black uppercase tracking-wider text-amber-950 flex items-center gap-1.5">
                <Settings2 size={16} className="text-amber-600" />
                <span>Código de Configuração da Máquina (6 dígitos)</span>
              </label>
              <span className="text-[11px] text-amber-800 font-semibold bg-amber-100 px-2 py-0.5 rounded-full border border-amber-300">
                Painel da Injetora
              </span>
            </div>

            <p className="text-[11px] text-slate-600 leading-relaxed">
              Cada caractere representa a posição no painel seletor da máquina (use <strong className="text-amber-900">-</strong> ou <strong className="text-amber-900">0</strong> para zero/desligado):
            </p>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  maxLength={6}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="ex: 1223-1 ou 233412"
                  value={formData.codigo_maquina}
                  onChange={(e) => setFormData({ ...formData, codigo_maquina: e.target.value.toUpperCase() })}
                  className="w-full font-mono font-black text-xl tracking-[0.3em] text-center uppercase py-2 bg-white border-2 border-amber-400 focus:border-amber-600 rounded-xl shadow-inner focus:outline-none focus:ring-2 focus:ring-amber-400/40 text-slate-900"
                />

                {formData.codigo_maquina && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, codigo_maquina: "" })}
                    className="p-2.5 bg-white hover:bg-slate-100 text-slate-500 hover:text-slate-800 border border-amber-200 rounded-xl transition cursor-pointer shrink-0"
                    title="Limpar código"
                  >
                    <RotateCcw size={16} />
                  </button>
                )}
              </div>

              {/* GUIA VISUAL DOS 6 PARÂMETROS DO CÓDIGO (INTERATIVO NO TABLET) */}
              <div className="grid grid-cols-6 gap-1 text-center pt-1">
                {[
                  { pos: 1, label: "Pressão 1", sub: "Inicial" },
                  { pos: 2, label: "Pressão 2", sub: "Injeção" },
                  { pos: 3, label: "Tempo", sub: "Injeção" },
                  { pos: 4, label: "Clamp", sub: "Aperto" },
                  { pos: 5, label: "Hold", sub: "Espera" },
                  { pos: 6, label: "Forward", sub: "Bico" }
                ].map((item, idx) => (
                  <button
                    key={item.pos}
                    type="button"
                    onClick={() => handleCycleDigit(idx)}
                    className="bg-white hover:bg-amber-100/50 border border-amber-200 rounded-lg p-1.5 shadow-2xs transition active:scale-95 cursor-pointer text-center select-none"
                    title={`Toque para alterar posição ${item.pos} (${item.label})`}
                  >
                    <div className="font-mono text-base font-black text-amber-900">
                      {digits[idx] && digits[idx] !== " " ? digits[idx] : "—"}
                    </div>
                    <div className="text-[9px] font-bold text-slate-700 leading-tight mt-0.5">{item.label}</div>
                    <div className="text-[8px] text-slate-500">{item.sub}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* TEMPERATURAS E VÁCUO */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Temperatura Tanque */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1">
                <Thermometer size={13} className="text-amber-600" />
                <span>Temp. Tanque (°C)</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="ex: 69"
                value={formData.temperatura_tanque}
                onChange={(e) => setFormData({ ...formData, temperatura_tanque: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition"
              />
              <div className="flex items-center gap-1 pt-0.5">
                {QUICK_TEMPS.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFormData({ ...formData, temperatura_tanque: t })}
                    className={`text-[10px] px-1.5 py-0.5 rounded border transition cursor-pointer ${
                      formData.temperatura_tanque === t 
                        ? "bg-amber-600 text-white border-amber-600 font-bold" 
                        : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                    }`}
                  >
                    {t}°
                  </button>
                ))}
              </div>
            </div>

            {/* Temperatura Bico */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1">
                <Thermometer size={13} className="text-red-500" />
                <span>Temp. Bico (°C)</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="ex: 71"
                value={formData.temperatura_bico}
                onChange={(e) => setFormData({ ...formData, temperatura_bico: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition"
              />
              <div className="flex items-center gap-1 pt-0.5">
                {QUICK_TEMPS.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFormData({ ...formData, temperatura_bico: t })}
                    className={`text-[10px] px-1.5 py-0.5 rounded border transition cursor-pointer ${
                      formData.temperatura_bico === t 
                        ? "bg-red-600 text-white border-red-600 font-bold" 
                        : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                    }`}
                  >
                    {t}°
                  </button>
                ))}
              </div>
            </div>

            {/* Tempo Vácuo */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1">
                <Clock size={13} className="text-blue-600" />
                <span>Tempo Vácuo (s)</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="ex: 3"
                value={formData.tempo_vacuo}
                onChange={(e) => setFormData({ ...formData, tempo_vacuo: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition"
              />
              <div className="flex items-center gap-1 pt-0.5">
                {QUICK_VACUOS.map(v => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setFormData({ ...formData, tempo_vacuo: v })}
                    className={`text-[10px] px-1.5 py-0.5 rounded border transition cursor-pointer ${
                      formData.tempo_vacuo === v 
                        ? "bg-blue-600 text-white border-blue-600 font-bold" 
                        : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                    }`}
                  >
                    {v}s
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* DADOS DE PRODUTO E PESO DA CERA */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Peso da Cera */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1">
                <Scale size={13} className="text-emerald-600" />
                <span>Peso Cera (g)</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="ex: 0.85"
                value={formData.peso_cera_g}
                onChange={(e) => setFormData({ ...formData, peso_cera_g: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition"
              />
            </div>

            {/* Peças por Molde / Cavidades com Steppers e Presets */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1">
                <Disc size={13} className="text-purple-600" />
                <span>Qtd Moldes / Peças</span>
              </label>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    const curr = parseInt(formData.qtd_moldes, 10) || 1;
                    setFormData(prev => ({ ...prev, qtd_moldes: String(Math.max(1, curr - 1)) }));
                  }}
                  className="w-8 h-8 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 font-black text-sm flex items-center justify-center select-none active:scale-95 cursor-pointer"
                  title="Diminuir"
                >
                  -
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="1"
                  value={formData.qtd_moldes}
                  onChange={(e) => setFormData({ ...formData, qtd_moldes: e.target.value.replace(/\D/g, "") })}
                  onBlur={() => {
                    if (!formData.qtd_moldes || !formData.qtd_moldes.trim() || parseInt(formData.qtd_moldes, 10) < 1) {
                      setFormData(prev => ({ ...prev, qtd_moldes: "1" }));
                    } else {
                      setFormData(prev => ({ ...prev, qtd_moldes: String(parseInt(formData.qtd_moldes, 10)) }));
                    }
                  }}
                  className="w-14 h-8 px-1 bg-slate-50 border border-slate-300 rounded-lg text-xs font-bold text-center focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition"
                />
                <button
                  type="button"
                  onClick={() => {
                    const curr = parseInt(formData.qtd_moldes, 10) || 0;
                    setFormData(prev => ({ ...prev, qtd_moldes: String(curr + 1) }));
                  }}
                  className="w-8 h-8 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 font-black text-sm flex items-center justify-center select-none active:scale-95 cursor-pointer"
                  title="Aumentar"
                >
                  +
                </button>
                <div className="flex items-center gap-1 pl-1">
                  {["1", "2", "4"].map(q => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setFormData({ ...formData, qtd_moldes: q })}
                      className={`h-8 px-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                        formData.qtd_moldes === q
                          ? "bg-purple-600 text-white shadow-2xs"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {q}x
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Tipo / Modelo da Joia */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1">
                  <span>Tipo / Modelo de Joia</span>
                </label>
                <button
                  type="button"
                  onClick={() => handleLoadPremade(formData.tipo_modelo)}
                  className="text-[10px] font-bold text-amber-800 hover:text-amber-950 bg-amber-100/70 hover:bg-amber-200/70 px-1.5 py-0.5 rounded border border-amber-300 transition cursor-pointer flex items-center gap-1"
                  title={`Carregar padrão de ${formData.tipo_modelo || "modelo"}`}
                >
                  <Zap size={10} className="fill-amber-700 text-amber-700" />
                  <span>Aplicar Padrão</span>
                </button>
              </div>
              <select
                value={formData.tipo_modelo}
                onChange={(e) => setFormData({ ...formData, tipo_modelo: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition cursor-pointer"
              >
                {MODELOS_CANONICOS.filter(m => m !== "Todos").map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          {/* SEÇÃO EXCLUSIVA DE ANÉIS E ALIANÇAS: GRADE DE AROS E TOLERÂNCIA */}
          {isModeloComMedida(formData.tipo_modelo) && (
            <div className="bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-slate-50 border-2 border-amber-400/60 rounded-xl p-4 space-y-3 shadow-xs">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-amber-500/20 text-amber-800 rounded-lg">
                    <Disc size={16} />
                  </div>
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-amber-950">
                      Medidas & Aros Físicos da Borracha
                    </h4>
                    <p className="text-[10px] text-amber-800/80">
                      Ligue os aros que a oficina possui molde físico para evitar falsos positivos
                    </p>
                  </div>
                </div>

                {/* ATALHOS RÁPIDOS */}
                <div className="flex items-center gap-1 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, aros_disponiveis: [...AROS_MAIS_VENDIDOS] }))}
                    className="text-[10px] bg-white hover:bg-amber-100 text-amber-900 px-2 py-0.5 rounded border border-amber-300 font-bold transition cursor-pointer"
                    title="Marcar aros 14, 16, 18, 20, 22"
                  >
                    Mais Vendidos
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, aros_disponiveis: [...AROS_PARES] }))}
                    className="text-[10px] bg-white hover:bg-amber-100 text-amber-900 px-2 py-0.5 rounded border border-amber-300 font-bold transition cursor-pointer"
                    title="Marcar todos os pares de 12 a 26"
                  >
                    Pares (12-26)
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, aros_disponiveis: [...AROS_PADRAO_GRADE] }))}
                    className="text-[10px] bg-white hover:bg-amber-100 text-amber-900 px-2 py-0.5 rounded border border-amber-300 font-bold transition cursor-pointer"
                    title="Marcar todos os aros de 10 a 32"
                  >
                    Todos
                  </button>
                  {formData.aros_disponiveis?.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, aros_disponiveis: [] }))}
                      className="text-[10px] bg-rose-50 hover:bg-rose-100 text-rose-700 px-2 py-0.5 rounded border border-rose-200 font-bold transition cursor-pointer"
                      title="Limpar seleção de aros"
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </div>

              {/* GRADE INTERATIVA DE CHIPS DE ARO (10 A 32) */}
              <div className="pt-1">
                <div className="grid grid-cols-6 sm:grid-cols-11 gap-1.5">
                  {AROS_PADRAO_GRADE.map(aroNum => {
                    const isSelected = (formData.aros_disponiveis || []).includes(aroNum);
                    return (
                      <button
                        key={aroNum}
                        type="button"
                        onClick={() => {
                          setFormData(prev => {
                            const curr = prev.aros_disponiveis || [];
                            const exists = curr.includes(aroNum);
                            const updated = exists ? curr.filter(a => a !== aroNum) : [...curr, aroNum];
                            return { ...prev, aros_disponiveis: updated.sort((a,b) => a - b) };
                          });
                        }}
                        className={`py-1.5 px-1 rounded-lg text-xs font-black transition-all flex flex-col items-center justify-center border cursor-pointer ${
                          isSelected
                            ? "bg-amber-600 hover:bg-amber-700 text-white border-amber-700 shadow-sm scale-105"
                            : "bg-white hover:bg-amber-50 text-slate-700 border-slate-200 hover:border-amber-300"
                        }`}
                        title={`Aro ${aroNum} ${isSelected ? "(Molde Físico Ativo)" : "(Clique para ativar molde)"}`}
                      >
                        <span>{aroNum}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* STATUS DA SELEÇÃO DE AROS */}
              <div className="flex items-center justify-between text-[11px] pt-1">
                <span className="font-semibold text-slate-600">
                  {(formData.aros_disponiveis || []).length > 0 ? (
                    <>
                      <strong className="text-amber-900 font-black">{(formData.aros_disponiveis || []).length}</strong> aros com molde físico:{" "}
                      <span className="font-mono text-amber-800 font-bold">{(formData.aros_disponiveis || []).join(", ")}</span>
                    </>
                  ) : (
                    <span className="text-slate-500 italic">
                      ⚠️ Nenhum aro marcado (molde genérico — não filtrará por aro)
                    </span>
                  )}
                </span>
              </div>

              {/* TOLERÂNCIA DE AJUSTE NA BANCADA */}
              <div className="pt-2 border-t border-amber-200/80 space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-black uppercase tracking-wider text-amber-950 flex items-center gap-1">
                    <span>Tolerância de Ajuste na Bancada</span>
                  </label>
                  <span className="text-[10px] text-amber-800 font-bold bg-amber-100 px-1.5 py-0.2 rounded">
                    Ourivesaria
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { val: 0, label: "Apenas Aro Exato", desc: "Tolerância 0 (Sem ajuste)" },
                    { val: 1, label: "± 1 Número", desc: "Ex: Molde 16 atende 15 e 17", recommended: true },
                    { val: 2, label: "± 2 Números", desc: "Ex: Molde 16 atende de 14 a 18" }
                  ].map(opt => {
                    const isSel = Number(formData.tolerancia_ajuste_bancada) === opt.val;
                    return (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, tolerancia_ajuste_bancada: opt.val }))}
                        className={`p-2 rounded-xl text-left border transition-all cursor-pointer ${
                          isSel
                            ? "bg-amber-600 text-white border-amber-700 shadow-sm"
                            : "bg-white text-slate-700 border-slate-200 hover:border-amber-300"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-black text-xs">{opt.label}</span>
                          {opt.recommended && (
                            <span className={`text-[9px] font-bold px-1 rounded ${isSel ? "bg-amber-800 text-amber-200" : "bg-amber-100 text-amber-800"}`}>
                              Padrão
                            </span>
                          )}
                        </div>
                        <p className={`text-[10px] mt-0.5 ${isSel ? "text-amber-100" : "text-slate-500"}`}>
                          {opt.desc}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* PEDRAS PADRÃO PARA PRÉ-CRAVAÇÃO COM STEPPERS E DIGITAÇÃO LIVRE */}
          <div className="space-y-2 pt-1 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                <Gem size={15} className="text-purple-600" />
                <span>Pedras Padrão para Pré-Cravação na Cera</span>
              </label>
              <button
                type="button"
                onClick={() => handleAddPedra()}
                className="text-xs font-bold text-purple-700 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 border border-purple-200 px-2.5 py-1 rounded-lg flex items-center gap-1 transition shadow-2xs cursor-pointer"
              >
                <Plus size={13} />
                <span>Adicionar Pedra</span>
              </button>
            </div>

            {/* PRESETS RÁPIDOS */}
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <span className="text-[10px] text-slate-400 font-semibold">Atalhos:</span>
              {POPULAR_STONES_PRESETS.map((pst, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleAddPedra(pst)}
                  className="text-[10px] bg-slate-100 hover:bg-purple-100 hover:text-purple-900 text-slate-700 px-2 py-0.5 rounded-full border border-slate-200 transition cursor-pointer"
                >
                  + {pst.tamanho} {pst.cor !== "Branca" ? `(${pst.cor})` : ""}
                </button>
              ))}
            </div>

            {/* LISTA DE PEDRAS CONFIGURADAS */}
            {formData.pedras_padrao.length === 0 ? (
              <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-3 text-center text-xs text-slate-400">
                Nenhuma pedra padrão cadastrada. Adicione se este modelo for pré-cravado na cera.
              </div>
            ) : (
              <div className="space-y-2">
                {formData.pedras_padrao.map((stone, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2.5 shadow-2xs flex-wrap sm:flex-nowrap"
                  >
                    {/* QUANTIDADE COM STEPPERS TOUCH-FRIENDLY E DIGITAÇÃO TOTALMENTE LIVRE */}
                    <div className="w-auto">
                      <span className="text-[9px] font-bold text-slate-400 block uppercase mb-0.5">Qtd</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            const curr = parseInt(stone.qtd, 10) || 1;
                            handleUpdatePedra(idx, "qtd", Math.max(1, curr - 1));
                          }}
                          className="w-8 h-8 rounded-lg bg-white hover:bg-slate-200 border border-slate-300 text-slate-700 font-black text-sm flex items-center justify-center select-none active:scale-95 cursor-pointer shadow-2xs"
                          title="Diminuir"
                        >
                          <Minus size={13} />
                        </button>

                        <input
                          type="text"
                          inputMode="numeric"
                          value={stone.qtd !== undefined && stone.qtd !== null ? stone.qtd : ""}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            // Permite apagar completamente (ficar vazio) sem forçar 1 imediatamente!
                            handleUpdatePedra(idx, "qtd", val);
                          }}
                          onBlur={() => {
                            // Apenas no blur aplica o fallback de 1 se estiver vazio ou inválido
                            if (stone.qtd === "" || isNaN(parseInt(stone.qtd, 10)) || parseInt(stone.qtd, 10) < 1) {
                              handleUpdatePedra(idx, "qtd", 1);
                            } else {
                              handleUpdatePedra(idx, "qtd", parseInt(stone.qtd, 10));
                            }
                          }}
                          className="w-12 h-8 px-1 bg-white border border-slate-300 rounded-lg text-xs font-black text-center focus:ring-2 focus:ring-amber-500 focus:outline-none"
                        />

                        <button
                          type="button"
                          onClick={() => {
                            const curr = parseInt(stone.qtd, 10) || 0;
                            handleUpdatePedra(idx, "qtd", curr + 1);
                          }}
                          className="w-8 h-8 rounded-lg bg-white hover:bg-slate-200 border border-slate-300 text-slate-700 font-black text-sm flex items-center justify-center select-none active:scale-95 cursor-pointer shadow-2xs"
                          title="Aumentar"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                    </div>

                    <div className="flex-1 min-w-[130px]">
                      <span className="text-[9px] font-bold text-slate-400 block uppercase mb-0.5">Tipo / Formato</span>
                      <input
                        type="text"
                        placeholder="ex: Zircônia Redonda"
                        value={stone.tipo || ""}
                        onChange={(e) => handleUpdatePedra(idx, "tipo", e.target.value)}
                        className="w-full h-8 px-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-amber-500 focus:outline-none"
                      />
                    </div>

                    <div className="w-24">
                      <span className="text-[9px] font-bold text-slate-400 block uppercase mb-0.5">Tamanho</span>
                      <input
                        type="text"
                        placeholder="ex: 1.5mm"
                        value={stone.tamanho || ""}
                        onChange={(e) => handleUpdatePedra(idx, "tamanho", e.target.value)}
                        className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-amber-500 focus:outline-none"
                      />
                    </div>

                    <div className="w-28">
                      <span className="text-[9px] font-bold text-slate-400 block uppercase mb-0.5">Cor</span>
                      <input
                        type="text"
                        placeholder="ex: Branca, Rubi"
                        value={stone.cor || ""}
                        onChange={(e) => handleUpdatePedra(idx, "cor", e.target.value)}
                        className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-amber-500 focus:outline-none"
                      />
                    </div>

                    <div className="pt-3.5">
                      <button
                        type="button"
                        onClick={() => handleRemovePedra(idx)}
                        className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                        title="Remover pedra"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* OBSERVAÇÕES E CUIDADOS DE INJEÇÃO */}
          <div className="space-y-1 pt-1 border-t border-slate-200">
            <label className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
              <Info size={14} className="text-amber-600" />
              <span>Observações e Cuidados de Injeção</span>
            </label>
            <textarea
              rows={2}
              placeholder="ex: Injetar com bico fino. Resfriar 15s antes de abrir para não empenar o canal..."
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition"
            />
          </div>

          {/* BOTÕES DE AÇÃO */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white rounded-xl text-xs font-black transition flex items-center gap-2 shadow-sm cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              <span>Salvar Parâmetros da Borracha</span>
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
